import { useQuery } from "@apollo/client/react";
import { Box, Collapse, Grid, Skeleton, useMediaQuery, useTheme } from "@mui/material";
import { BackwardBack, Checkmark } from "@pmag/icons";
// eslint-disable-next-line no-restricted-imports
import { Button, DataGridCustomFilterButton, DataGridSearchBox, InlineError, type PropsWithDataProperties, Typography } from "@pmag/ui-components";
import { AutocompleteField, DatePickerField, FormHookForm, SelectField } from "@pmag/ui-components-react-hook-form";
import { type GQLModelSpecification, type GQLUsageListToolbarQuery, type GQLUsageListToolbarQueryVariables } from "@src/graphql.generated";
import { usageListToolbarQuery } from "@src/pages/compatibilityCheck/components/usageList/toolbar/Toolbar.gql";
import { resolveComponentGroupAutocompleteOptions } from "@src/pages/compatibilityCheck/components/usageList/toolbar/Toolbar.utils";
import {
    defaultToolbarFormState,
    type SessionFilterState,
    type ToolbarFormState,
} from "@src/pages/compatibilityCheck/components/usageList/UsageListFormSchema";
import { isEqual } from "lodash";
import * as React from "react";
import { useForm, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { FilterPanelCard, GroupHeaderBox, GroupItems, ToolbarOuterBox } from "./Toolbar.styles";

export const dataTestId = {
    toolbar: "sparePartsFinder.usageList.toolbar",
    toolbarSkeleton: "sparePartsFinder.usageList.toolbarSkeleton",
    searchForm: "sparePartsFinder.usageList.toolbar.searchForm",
    actions: "sparePartsFinder.usageList.toolbar.actions",
    resetButton: "sparePartsFinder.usageList.toolbar.resetButton",
    applyButton: "sparePartsFinder.usageList.toolbar.applyButton",
    filterPanel: "sparePartsFinder.usageList.toolbar.filterPanel",
    filterPanelButton: "sparePartsFinder.usageList.toolbar.filterPanel.filterButton",
    componentGroupAutocompleteOptionGroup: "sparePartsFinder.usageList.toolbar.componentGroupAutocompleteOptionGroup",
    inlineError: "sparePartsFinder.usageList.toolbar.inlineError",
};

const isDefaultState = (state: ToolbarFormState) => {
    return isEqual(state, defaultToolbarFormState);
};

export interface ToolbarProps extends PropsWithDataProperties {
    appliedFilter: SessionFilterState;
    onFilterChange: (filter: ToolbarFormState) => void;
    onSearchChange: (search: string) => void;
    searchText: string;
}

export const Toolbar: React.FunctionComponent<ToolbarProps> = ({ appliedFilter, onFilterChange, searchText, onSearchChange }) => {
    const intl = useIntl();
    const theme = useTheme();
    const addFlexibleSpace = useMediaQuery(theme.breakpoints.up("md"));
    const [filterOpen, setFilterOpen] = React.useState(false);

    const {
        data: toolbarData,
        loading,
        error,
        refetch,
    } = useQuery<GQLUsageListToolbarQuery, GQLUsageListToolbarQueryVariables>(usageListToolbarQuery);

    const componentGroupAutocompleteOptions = React.useMemo(() => {
        return resolveComponentGroupAutocompleteOptions(toolbarData?.componentGroups?.nodes || []);
    }, [toolbarData?.componentGroups?.nodes]);

    const defaultValues = React.useMemo<ToolbarFormState>(() => {
        const componentGroup =
            componentGroupAutocompleteOptions.find((option) => {
                return option.value === appliedFilter?.componentGroup;
            }) || null;

        return {
            modelSpecification: appliedFilter.modelSpecification ?? defaultToolbarFormState.modelSpecification,
            modelYear: appliedFilter.modelYear ?? defaultToolbarFormState.modelYear,
            componentGroup: componentGroup,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [componentGroupAutocompleteOptions]);

    const { reset, getValues, formState, watch, ...formMethods } = useForm<ToolbarFormState>({ defaultValues });

    const values = useWatch({ control: formMethods.control });

    if (loading) {
        return (
            <ToolbarOuterBox>
                <Skeleton height={35} data-testid={dataTestId.toolbarSkeleton} />
            </ToolbarOuterBox>
        );
    }

    if (error) {
        return (
            <ToolbarOuterBox>
                <InlineError onRetry={refetch} variant="small" data-testid={dataTestId.inlineError} />
            </ToolbarOuterBox>
        );
    }

    return (
        <ToolbarOuterBox data-testid={dataTestId.toolbar}>
            <Grid
                container
                columns={{
                    lg: 12,
                    md: 12,
                    sm: 8,
                    xl: 12,
                    xs: 4,
                }}
                alignItems="center"
                spacing={2}
            >
                <Grid item md={5} sm={6} xs={3}>
                    <DataGridSearchBox
                        placeholder={intl.formatMessage({
                            defaultMessage: "Search...",
                            id: "sparePartsFinder.usageList.toolbar.searchPlaceholder",
                        })}
                        value={searchText}
                        onChange={onSearchChange}
                    />
                </Grid>

                <Grid item md="auto" sm={2} xs={1}>
                    <DataGridCustomFilterButton
                        data-testid={dataTestId.filterPanelButton}
                        open={filterOpen}
                        onClick={() => setFilterOpen(!filterOpen)}
                    />
                </Grid>
            </Grid>

            <Collapse in={filterOpen}>
                <FilterPanelCard data-testid={dataTestId.filterPanel}>
                    <FormHookForm<ToolbarFormState>
                        {...formMethods}
                        watch={watch}
                        reset={reset}
                        formState={formState}
                        getValues={getValues}
                        onSubmit={formMethods.handleSubmit((values: ToolbarFormState) => {
                            onFilterChange(values);
                            reset(values, { keepIsSubmitted: true });
                        })}
                        dataTestId={dataTestId.searchForm}
                    >
                        <Grid
                            container
                            columns={{
                                lg: 12,
                                md: 12,
                                sm: 8,
                                xl: 12,
                                xs: 4,
                            }}
                            spacing={4}
                        >
                            <Grid item xs={4} sm={4} md={6} lg={3}>
                                <AutocompleteField
                                    fullWidth
                                    name="componentGroup"
                                    label={
                                        <FormattedMessage
                                            id="sparePartsFinder.usageList.toolbar.form.componentGroup"
                                            defaultMessage="Component Group"
                                        />
                                    }
                                    renderGroup={({ key, group, children }) => (
                                        <Box key={key} data-testid={dataTestId.componentGroupAutocompleteOptionGroup}>
                                            <GroupHeaderBox>
                                                <Typography variant="heading-08">{group}</Typography>
                                            </GroupHeaderBox>

                                            <GroupItems>{children}</GroupItems>
                                        </Box>
                                    )}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    groupBy={(option) => option.type}
                                    options={componentGroupAutocompleteOptions}
                                />
                            </Grid>

                            <Grid item xs={4} sm={12} md={6} lg={3}>
                                <Grid
                                    container
                                    columns={{
                                        lg: 12,
                                        md: 12,
                                        sm: 8,
                                        xl: 12,
                                        xs: 4,
                                    }}
                                    spacing={4}
                                >
                                    <Grid item xs={4} sm={4} md={6}>
                                        <SelectField
                                            name="modelSpecification"
                                            label={
                                                <FormattedMessage
                                                    id="sparePartsFinder.usageList.toolbar.form.modelVariant"
                                                    defaultMessage="Model Variant"
                                                />
                                            }
                                            fullWidth
                                            placeholder={intl.formatMessage({
                                                id: "sparePartsFinder.usageList.toolbar.form.modelVariant.placeholder",
                                                defaultMessage: "Select ...",
                                            })}
                                            options={[
                                                {
                                                    label: intl.formatMessage({
                                                        id: "sparePartsFinder.usageList.toolbar.form.modelVariant.all",
                                                        defaultMessage: "All",
                                                    }),
                                                    value: "",
                                                },
                                                ...(toolbarData?.modelSpecifications?.nodes || []).map((item: GQLModelSpecification) => ({
                                                    label: item.name,
                                                    value: item.name,
                                                })),
                                            ]}
                                        />
                                    </Grid>

                                    <Grid item xs={4} sm={4} md={6}>
                                        <DatePickerField
                                            views={["year"]}
                                            maxDate={new Date()}
                                            name="modelYear"
                                            label={
                                                <FormattedMessage
                                                    id="sparePartsFinder.usageList.toolbar.form.modelYear"
                                                    defaultMessage="Model Year"
                                                />
                                            }
                                            placeholder={intl.formatMessage({
                                                id: "sparePartsFinder.usageList.toolbar.form.modelYear.placeholder",
                                                defaultMessage: "Select year",
                                            })}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid
                            container
                            columns={{
                                lg: 12,
                                md: 12,
                                sm: 8,
                                xl: 12,
                                xs: 4,
                            }}
                            pt={3}
                        >
                            {addFlexibleSpace && <Grid item xs />}

                            <Grid item xs={4} md="auto">
                                <Button
                                    variant="ghost"
                                    disabled={isDefaultState(values)}
                                    onClick={() => {
                                        reset(defaultToolbarFormState);
                                        onFilterChange(getValues());
                                    }}
                                    fullWidth
                                    icon={<BackwardBack size="small" />}
                                    size="small"
                                    data-testid={dataTestId.resetButton}
                                >
                                    <FormattedMessage
                                        id="sparePartsFinder.usageList.toolbar.form.button.resetAllFields"
                                        defaultMessage="Reset all fields"
                                    />
                                </Button>
                            </Grid>

                            <Grid item xs={4} md="auto">
                                <Button
                                    variant="primary"
                                    type="submit"
                                    icon={<Checkmark size="small" />}
                                    size="small"
                                    fullWidth
                                    disabled={!formState.isDirty}
                                    data-testid={dataTestId.applyButton}
                                >
                                    <FormattedMessage id="sparePartsFinder.usageList.toolbar.form.button.apply" defaultMessage="Apply" />
                                </Button>
                            </Grid>
                        </Grid>
                    </FormHookForm>
                </FilterPanelCard>
            </Collapse>
        </ToolbarOuterBox>
    );
};
